import React, { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Engine } from "@designboticteam/core";

type EngineContextValue<TApi> = {
	api: TApi | null;
};

type InternalContextValue<TApi> = EngineContextValue<TApi> & {
	attachApi: (api: TApi | null) => void;
};

function createEngineContext<TApi extends { dispose: () => void }>(use: (builder: Engine) => Engine<TApi>) {
	const Context = createContext<InternalContextValue<TApi> | null>(null);

	const EngineContext: React.FC<PropsWithChildren> = ({ children }) => {
		const [api, setApi] = useState<TApi | null>(null);
		const attachApi = useCallback((value: TApi | null) => setApi(value), []);

		const value = useMemo<InternalContextValue<TApi>>(() => ({ api, attachApi }), [api, attachApi]);

		return <Context.Provider value={value}>{children}</Context.Provider>;
	};

	const EngineComponent: React.FC = () => {
		const wrapperRef = useRef<HTMLDivElement>(null);
		const context = useContext(Context);

		if (context === null) {
			throw new Error("<Engine> must be used within <EngineContext>");
		}

		const { attachApi } = context;

		useEffect(() => {
			if (wrapperRef.current === null) {
				return;
			}

			const engine = use(new Engine({ wrapperElement: wrapperRef.current })).build();
			attachApi(engine);

			return () => {
				engine.dispose();
				attachApi(null);
			};
		}, [attachApi]);

		return <div ref={wrapperRef} style={{ width: "100%", height: "100%" }} />;
	};

	const EngineGate: React.FC<PropsWithChildren> = ({ children }) => {
		const context = useContext(Context);

		if (context === null) {
			throw new Error("<EngineGate> must be used within <EngineContext>");
		}

		const isReady = context.api !== null;

		useEffect(() => {
			if (isReady) {
				document.dispatchEvent(new CustomEvent("__e2e__:engine:ready", { bubbles: true }));
			}
		}, [isReady]);

		if (isReady === false) {
			return null;
		}

		return children;
	};

	function useEngineApi(): TApi {
		const api = useContext(Context)?.api ?? null;

		if (api === null) {
			throw new Error("useEngineApi must be used within <EngineGate> or after engine is ready");
		}

		return api;
	}

	return { EngineContext, EngineGate, EngineComponent, useEngineApi };
}

export { createEngineContext };
